import React, { useCallback } from 'react'
import { Avatar, Button, Flex, Form, Input, Spin, message } from 'antd'
import { DesktopOutlined, LogoutOutlined, ShopOutlined, SwapOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'
import { Helmet } from 'react-helmet'
import { generateCardName } from '@/utils/helper'
import { useRegisterPosDevice } from '@/utils/queries/posDevices'
import { getBrowserDeviceInfo } from '@/utils/shiftDevices'
import { POS_ONLINE_HEADER } from '@/utils/static'
import styles from '../ZeroLoading/style.module.scss'

/**
 * Halaman pendaftaran browser sebagai device POS (sama dengan kledopos-mobile). Tampil
 * setelah outlet dipilih bila BE melaporkan `pos_device.registered === false`;
 * `checking` = status masih diperiksa. Sukses → `PUT /pos/devices` → `onRegistered`.
 */
const RegisterDevicePage = ({
  deviceId,
  outlet,
  checking,
  onRegistered,
  onChangeOutlet,
  onLogout,
}) => {
  const { formatMessage } = useIntl()
  const t = (id) => formatMessage({ id })
  const [form] = Form.useForm()
  const { mutate, isLoading } = useRegisterPosDevice()

  const handleSubmit = useCallback(
    async ({ nickname }) => {
      const cleanNickname = String(nickname || '').trim()
      const deviceInfo = await getBrowserDeviceInfo()
      mutate(
        {
          device_id: deviceId,
          nickname: cleanNickname,
          device_info: deviceInfo || undefined,
          apiOptions: POS_ONLINE_HEADER,
        },
        {
          onSuccess: (response) => {
            if (response?.data?.success === false) {
              message.error(
                response?.data?.message || formatMessage({ id: 'pos.device.register_failed' }),
              )
              return
            }
            onRegistered(cleanNickname)
            message.success(formatMessage({ id: 'pos.device.register_success' }))
          },
          onError: (error) => {
            message.error(
              error?.data?.message || formatMessage({ id: 'pos.device.register_failed' }),
            )
          },
        },
      )
    },
    [deviceId, formatMessage, mutate, onRegistered],
  )

  return (
    <>
      <Helmet title={t('pos.device.register_title')} />
      <div className={styles.gate}>
        <img
          src="resources/images/login-bg-decor.svg?v=3"
          alt=""
          aria-hidden="true"
          className={styles.bgDecor}
        />
        <div className={styles.gateCard}>
          <div
            className={`${styles.heroIcon} ${checking ? styles.running : ''}`}
            aria-hidden="true"
          >
            <DesktopOutlined />
          </div>

          {outlet?.name && (
            <Flex justify="center">
              <span className={styles.outletChip}>
                <Avatar size={22} src={outlet.photo || undefined} icon={<ShopOutlined />}>
                  {generateCardName(outlet.name)}
                </Avatar>
                {outlet.name}
              </span>
            </Flex>
          )}

          {checking ? (
            <>
              <h3 className={`ant-typography ${styles.title}`}>{t('pos.device.checking')}</h3>
              <p className={styles.subtitle}>{t('pos.device.checking_desc')}</p>
              <Spin className={styles.progress} />
            </>
          ) : (
            <>
              <h3 className={`ant-typography ${styles.title}`}>{t('pos.device.register_title')}</h3>
              <p className={styles.subtitle}>{t('pos.device.register_desc')}</p>

              <Form
                form={form}
                layout="vertical"
                className={styles.form}
                onFinish={handleSubmit}
                requiredMark={false}
              >
                <Form.Item
                  name="nickname"
                  label={t('pos.device.nickname')}
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: t('pos.device.nickname_required'),
                    },
                    { max: 50 },
                  ]}
                >
                  <Input
                    size="large"
                    placeholder={t('pos.device.nickname_placeholder')}
                    prefix={<DesktopOutlined style={{ color: 'rgba(0,0,0,0.35)' }} />}
                    autoFocus
                    maxLength={50}
                  />
                </Form.Item>
                <Button type="primary" size="large" htmlType="submit" loading={isLoading} block>
                  {t('pos.device.register')}
                </Button>
              </Form>

              <div className={styles.deviceId}>
                {t('pos.device.device_id')}: {deviceId}
              </div>
            </>
          )}

          <Flex justify="center" gap={4} wrap="wrap" style={{ marginTop: 8 }}>
            {onChangeOutlet && (
              <Button type="link" icon={<SwapOutlined />} onClick={onChangeOutlet}>
                {t('change_outlet')}
              </Button>
            )}
            {onLogout && (
              <Button type="link" icon={<LogoutOutlined />} onClick={onLogout}>
                Logout
              </Button>
            )}
          </Flex>
        </div>
        <div className={styles.footer}>
          Powered by <strong>Kledo</strong> · ©{` ${new Date().getFullYear()} Kledo`}
        </div>
      </div>
    </>
  )
}

export default RegisterDevicePage
